import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, UserPlus, Phone, CreditCard, MapPin, Contact } from 'lucide-react';
import { Card, Button, Input, Badge } from '../../components/ui/index.jsx';

import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';


const DriverList = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'inactive', 'all'
    const [currentPage, setCurrentPage] = useState(1);
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('drivers', 'create');
    const canUpdate = hasPermission('drivers', 'update');
    const canDelete = hasPermission('drivers', 'delete');
    const { success, error: toastError } = useToast();

    
    const { data: driversResponse, loading, refresh: refreshDrivers } = useGet(`/drivers?page=${currentPage}&search=${searchTerm}&status=${statusFilter}`);

    const drivers = driversResponse?.data || [];
    const pagination = driversResponse?.meta || null;

    const filteredDrivers = Array.isArray(drivers) ? drivers : [];

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذا السائق؟')) {
            try {
                await api.delete(`/drivers/${id}`);
                success('تم حذف السائق بنجاح');
                refreshDrivers();
            } catch (err) { toastError(err.response?.data?.message || 'خطأ أثناء الحذف'); }
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl text-slate-800 font-bold">قائمة السائقين</h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1">إدارة بيانات سائقي الحافلات المدرسية</p>
                </div>
                {canCreate && (
                    <Button onClick={() => { setSelectedDriver(null); setIsModalOpen(true); }}>
                        <Plus className="w-5 h-5" /> إضافة سائق
                    </Button>
                )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
                <div className="flex-1">
                    <Card className="p-3">
                        <Input 
                            placeholder="بحث باسم السائق أو البطاقة الوطنية..." 
                            icon={Search}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </Card>
                </div>
                
                {/* Status Filter Tabs */}
                <div className="flex items-center gap-1.5 bg-slate-100/80 p-1.5 rounded-xl border border-slate-200/60 self-start sm:self-auto">
                    <button
                        type="button"
                        onClick={() => { setStatusFilter('active'); setCurrentPage(1); }}
                        className={`px-4 py-1.5 text-xs rounded-lg font-medium transition-all ${
                            statusFilter === 'active' 
                            ? 'bg-white text-emerald-600 shadow-sm border border-slate-200/50' 
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        النشطون
                    </button>
                    <button
                        type="button"
                        onClick={() => { setStatusFilter('inactive'); setCurrentPage(1); }}
                        className={`px-4 py-1.5 text-xs rounded-lg font-medium transition-all ${
                            statusFilter === 'inactive' 
                            ? 'bg-white text-red-600 shadow-sm border border-slate-200/50' 
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        غير النشطين
                    </button>
                    <button
                        type="button"
                        onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
                        className={`px-4 py-1.5 text-xs rounded-lg font-medium transition-all ${
                            statusFilter === 'all' 
                            ? 'bg-white text-slate-800 shadow-sm border border-slate-200/50' 
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        الجميع
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    Array(3).fill(0).map((_, i) => <div key={i} className="h-48 bg-slate-100 animate-pulse rounded-lg" />)
                ) : filteredDrivers.length === 0 ? (
                    <div className="col-span-full py-12 text-center text-slate-400">
                        {statusFilter === 'active' ? 'لا يوجد سائقين نشطين حالياً' : 'لا يوجد سائقين في هذه القائمة'}
                    </div>
                ) : (
                    filteredDrivers.map(driver => {
                        const todayStr = new Date().toISOString().split('T')[0];
                        const isDriverActive = !driver.end_date || driver.end_date > todayStr;
                        return (
                            <Card key={driver.id} className="p-4 transition-all h-full flex flex-col group relative">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-orange-50 text-primary rounded-lg flex items-center justify-center font-bold text-lg shrink-0">
                                            {driver.full_name.charAt(0)}
                                        </div>
                                        <div>
                                            <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                                isDriverActive 
                                                ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' 
                                                : 'bg-red-50 text-red-600 border border-red-200'
                                            }`}>
                                                {isDriverActive ? 'نشط' : 'غير نشط'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="flex gap-1">
                                        {canUpdate && (
                                            <button onClick={() => { setSelectedDriver(driver); setIsModalOpen(true); }} className="p-2 text-slate-400 hover:text-primary transition-colors border border-slate-50 rounded-lg bg-slate-50/50"><Edit size={18} /></button>
                                        )}
                                        {canDelete && (
                                            <button onClick={() => handleDelete(driver.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors border border-slate-50 rounded-lg bg-slate-50/50"><Trash2 size={18} /></button>
                                        )}
                                    </div>
                                </div>
                                
                                <h3 className="text-lg font-bold text-slate-800 mb-1">{driver.full_name}</h3>
                                <p className="text-xs text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                                    <Contact size={12} /> {driver.cin} {driver.permis ? `| ${driver.permis}` : ''}
                                </p>

                                <div className="space-y-2 mt-auto">
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                        <Phone size={14} className="text-primary" /> <span>{driver.phone || 'لا يوجد رقم هاتف'}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-slate-500">
                                        <MapPin size={14} className="text-slate-400" /> <span className="truncate">{driver.address || 'لا يوجد عنوان'}</span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4 text-xs mt-2 pt-2 border-t border-slate-100">
                                        {driver.salary && (
                                            <div className="flex items-center gap-1 text-slate-600">
                                                <span className="font-bold">الراتب:</span> {driver.salary} درهم
                                            </div>
                                        )}
                                        {driver.start_date && (
                                            <div className="flex items-center gap-1 text-emerald-600">
                                                <span className="font-bold">البداية:</span> {driver.start_date}
                                            </div>
                                        )}
                                        {driver.end_date && (
                                            <div className="flex items-center gap-1 text-red-500">
                                                <span className="font-bold">الخروج:</span> {driver.end_date}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        );
                    })
                )}
            </div>

            {/* Pagination Controls */}
            {pagination && pagination.last_page > 1 && (
                <Card className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                    <div className="text-xs text-slate-500 font-normal">
                        عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> سائق
                    </div>
                    <div className="flex items-center gap-2">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="h-8 px-3 text-xs"
                        >
                            السابق
                        </Button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                let pageNum = i + 1;
                                if (pagination.last_page > 5 && currentPage > 3) {
                                    pageNum = currentPage - 3 + i + 1;
                                    if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-8 h-8 rounded-lg text-xs transition-all ${
                                            currentPage === pageNum 
                                            ? 'bg-primary text-white shadow-sm font-medium' 
                                            : 'text-slate-500 hover:bg-slate-100'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                            disabled={currentPage === pagination.last_page}
                            className="h-8 px-3 text-xs"
                        >
                            التالي
                        </Button>
                    </div>
                </Card>
            )}

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedDriver ? 'تعديل بيانات السائق' : 'إضافة سائق جديد'}>
                <DriverForm 
                    key={selectedDriver?.id || 'new-driver'}
                    driver={selectedDriver} 
                    onSuccess={() => { setIsModalOpen(false); refreshDrivers(); }} 
                />
            </Modal>
        </div>
    );
};

const DriverForm = ({ driver, onSuccess }) => {
    const [formData, setFormData] = useState({
        full_name: driver?.full_name || '',
        code: driver?.code || '',
        cin: driver?.cin || '',
        permis: driver?.permis || '',
        phone: driver?.phone || '',
        address: driver?.address || '',
        salary: driver?.salary || '',
        start_date: driver?.start_date || '',
        end_date: driver?.end_date || ''
    });

    useEffect(() => {
        setFormData({
            full_name: driver?.full_name || '',
            code: driver?.code || '',
            cin: driver?.cin || '',
            permis: driver?.permis || '',
            phone: driver?.phone || '',
            address: driver?.address || '',
            salary: driver?.salary || '',
            start_date: driver?.start_date || '',
            end_date: driver?.end_date || ''
        });
    }, [driver]);

    const [loading, setLoading] = useState(false);
    const { success, error: toastError } = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const payload = {
            ...formData,
            salary: formData.salary !== '' ? formData.salary : null,
            start_date: formData.start_date || null,
            end_date: formData.end_date || null
        };

        try {
            if (driver) {
                await api.put(`/drivers/${driver.id}`, payload);
                success('تم تحديث بيانات السائق بنجاح');
            } else {
                await api.post('/drivers', payload);
                success('تم إضافة السائق بنجاح');
            }
            onSuccess();
        } catch (err) { 
            toastError(err.response?.data?.message || 'خطأ أثناء الحفظ'); 
        }
        finally { setLoading(false); }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="الاسم الكامل" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} required/>
                <Input label="كود السائق" value={formData.code} onChange={e => setFormData({...formData, code: e.target.value})} required/>
                <Input label="رقم البطاقة (CIN)" value={formData.cin} onChange={e => setFormData({...formData, cin: e.target.value})} required/>
                <Input label="رقم رخصة السياقة" value={formData.permis} onChange={e => setFormData({...formData, permis: e.target.value})} />
                <Input label="الهاتف" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                <Input label="العنوان" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
                <Input label="الراتب (درهم)" type="number" step="0.01" value={formData.salary} onChange={e => setFormData({...formData, salary: e.target.value})} />
                <Input label="تاريخ البداية (الدخول)" type="date" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} />
                <Input label="تاريخ الخروج" type="date" value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} />
            </div>
            <Button type="submit" isLoading={loading} className="w-full">حفظ البيانات</Button>
        </form>
    );
};

export default DriverList;
